// src/components/payment/Payment.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Example placeholders for images
import tshirtFront from '../../assets/images/cat-fashion.jpg';
import tshirtThumb from '../../assets/images/cat-tech.jpg';
// For the recommended items on confirmation step
import recommended1 from '../../assets/images/cat-hampers.jpg';
import recommended2 from '../../assets/images/cat-keychains.jpg';
import recommended3 from '../../assets/images/cat-mugs.jpg';

const Payment = () => {
  // Step: 1 = checkout, 2 = confirmation
  const [step, setStep] = useState(1);
  // Payment method selection: "stripe" or "razorpay"
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("stripe");
  // Loading state for processing payment
  const [processing, setProcessing] = useState(false);

  // Function to simulate processing with Stripe API
  const processStripePayment = async () => {
    // Insert your Stripe API integration code here.
    // For example, you might use Stripe.js:
    // const stripe = await loadStripe('your-publishable-key');
    // const { error } = await stripe.redirectToCheckout({ sessionId: 'your-session-id' });
    console.log("Processing payment with Stripe...");
    // Simulate API delay
    return new Promise((resolve) => setTimeout(resolve, 1500));
  };

  // Function to simulate processing with Razorpay API
  const processRazorpayPayment = async () => {
    // Insert your Razorpay API integration code here.
    // For example, you might configure Razorpay options and open the checkout modal:
    // const options = { key: 'your-key', amount: amountInPaise, currency: 'INR', ... };
    // const rzp = new Razorpay(options);
    // rzp.open();
    console.log("Processing payment with Razorpay...");
    // Simulate API delay
    return new Promise((resolve) => setTimeout(resolve, 1500));
  };

  // Handler for "Pay Now" button on Step 1
  const handlePayNow = async () => {
    setProcessing(true);
    try {
      if (selectedPaymentMethod === "stripe") {
        await processStripePayment();
      } else if (selectedPaymentMethod === "razorpay") {
        await processRazorpayPayment();
      }
      // After successful payment processing, move to confirmation step
      setStep(2);
    } catch (err) {
      console.error("Payment processing error:", err);
      // Here you could set an error state to show a message
    }
    setProcessing(false);
  };

  return (
    <div className="bg-[#f2ebe8] min-h-screen py-8">
      <div className="max-w-screen-xl mx-auto px-4">
        
        {/* -------- Progress Steps -------- */}
        <div className="flex items-center justify-center mb-8">
          {/* Step 1 */}
          <div className="text-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto ${step === 1 ? 'bg-blue-600 text-white' : 'bg-gray-400 text-white opacity-50'}`}>
              1
            </div>
            <p className={`text-sm mt-2 font-semibold ${step === 1 ? 'text-black' : 'opacity-50'}`}>Check Out</p>
          </div>

          {/* Connector Line */}
          <div className="flex-1 mx-2 border-t-2 border-gray-300"></div>

          {/* Step 2 */}
          <div className="text-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto ${step === 2 ? 'bg-blue-600 text-white' : 'bg-gray-400 text-white opacity-50'}`}>
              2
            </div>
            <p className={`text-sm mt-2 font-semibold ${step === 2 ? 'text-black' : 'opacity-50'}`}>Confirmation</p>
          </div>
        </div>

        {/* -------- Conditional Rendering -------- */}
        {step === 1 ? (
          /* ========================
             STEP 1: CHECKOUT FORM
             ======================== */
          <div>
            {/* Top Attributes Section */}
            <div className="bg-white rounded shadow p-4 mb-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Quantity */}
                <div>
                  <h3 className="font-bold text-gray-700 mb-2">Quantity</h3>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> S
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> M
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> L
                  </label>
                </div>
                {/* Fabric Material */}
                <div>
                  <h3 className="font-bold text-gray-700 mb-2">Fabric Material</h3>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Polyester
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Cotton/Poly Blend
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> 100% Cotton
                  </label>
                </div>
                {/* Print Method */}
                <div>
                  <h3 className="font-bold text-gray-700 mb-2">Print Method</h3>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Direct to Garment
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Screen Print
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Embroidery
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Heat Transfer
                  </label>
                </div>
                {/* Print Placement */}
                <div>
                  <h3 className="font-bold text-gray-700 mb-2">Print Placement</h3>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Front (10"x10")
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Back (10"x10")
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Left Sleeve
                  </label>
                  <label className="block">
                    <input type="checkbox" className="mr-2" /> Right Sleeve
                  </label>
                </div>
              </div>
            </div>

            {/* Main Content Row: Left Product Preview & Right Checkout Card */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Product Preview */}
              <div className="flex flex-col">
                <div className="bg-white rounded shadow p-4 flex justify-center items-center mb-4">
                  <img src={tshirtFront} alt="T-shirt front" className="max-h-80 object-contain" />
                </div>
                <div className="flex space-x-2 mb-4">
                  {[...Array(4)].map((_, i) => (
                    <img
                      key={i}
                      src={tshirtThumb}
                      alt={`T-shirt thumb ${i}`}
                      className="h-16 w-16 object-cover cursor-pointer bg-white border border-gray-300 rounded"
                    />
                  ))}
                </div>
                <div className="flex space-x-4">
                  <button className="flex-1 bg-blue-700 text-white font-semibold py-2 rounded hover:bg-blue-800">
                    Front
                  </button>
                  <button className="flex-1 bg-white border border-blue-700 text-blue-700 font-semibold py-2 rounded hover:bg-blue-50">
                    Back
                  </button>
                </div>
              </div>

              {/* Right: Checkout Card */}
              <div>
                <div className="bg-white rounded shadow p-4 mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">Checkout</h2>

                  {/* Order Summary */}
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">Order Summary</h3>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Hoodies</span>
                      <span>$23.00</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Shipping Fee</span>
                      <span>$6</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between text-sm font-bold">
                      <span>Total</span>
                      <span>$29</span>
                    </div>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">Payment Method</h3>
                    <div className="flex space-x-2">
                      {/* Stripe Icon */}
                      <div
                        onClick={() => setSelectedPaymentMethod("stripe")}
                        className={`cursor-pointer p-1 rounded border ${
                          selectedPaymentMethod === "stripe" ? "border-blue-600" : "border-transparent"
                        }`}
                      >
                        <img
                          src="https://img.icons8.com/color/48/stripe.png"
                          alt="Stripe"
                          className="h-8"
                        />
                      </div>
                      {/* Razorpay Icon */}
                      <div
                        onClick={() => setSelectedPaymentMethod("razorpay")}
                        className={`cursor-pointer p-1 rounded border ${
                          selectedPaymentMethod === "razorpay" ? "border-blue-600" : "border-transparent"
                        }`}
                      >
                        <img
                          src="https://img.icons8.com/color/48/razorpay.png"
                          alt="Razorpay"
                          className="h-8"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card Details (for Stripe, if needed) */}
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">Card Details</h3>
                    <div className="mb-2">
                      <label className="text-sm text-gray-600">Card Number</label>
                      <input
                        type="text"
                        placeholder="xxxx-xxxx-xxxx-xxxx"
                        className="w-full mt-1 p-2 border rounded"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-sm text-gray-600">Expiry Date</label>
                        <input
                          type="text"
                          placeholder="MM/YY"
                          className="w-full mt-1 p-2 border rounded"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">CVV</label>
                        <input
                          type="text"
                          placeholder="XXX"
                          className="w-full mt-1 p-2 border rounded"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Phone Code</label>
                      <input
                        type="text"
                        placeholder="+1"
                        className="w-full mt-1 p-2 border rounded"
                      />
                    </div>
                  </div>

                  {/* Shipping Information */}
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">Shipping Information</h3>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-sm text-gray-600">First Name</label>
                        <input
                          type="text"
                          placeholder="John"
                          className="w-full mt-1 p-2 border rounded"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Last Name</label>
                        <input
                          type="text"
                          placeholder="Doe"
                          className="w-full mt-1 p-2 border rounded"
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="text-sm text-gray-600">Street</label>
                      <input
                        type="text"
                        placeholder="123 Main St."
                        className="w-full mt-1 p-2 border rounded"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-sm text-gray-600">City</label>
                        <input
                          type="text"
                          placeholder="Austin"
                          className="w-full mt-1 p-2 border rounded"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Country</label>
                        <input
                          type="text"
                          placeholder="USA"
                          className="w-full mt-1 p-2 border rounded"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Terms & Pay Now */}
                  <div className="mb-4 text-sm">
                    <label className="inline-flex items-center">
                      <input type="checkbox" className="mr-2" />
                      I agree to{' '}
                      <Link to="/terms" className="ml-1 text-blue-600 hover:underline">
                        the Terms &amp; Conditions
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="ml-1 text-blue-600 hover:underline">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  <button
                    onClick={handlePayNow}
                    disabled={processing}
                    className="w-full bg-yellow-500 text-white font-bold py-2 rounded hover:bg-yellow-600"
                  >
                    {processing ? "Processing..." : "Pay Now"}
                  </button>
                </div>
              </div>
            </div>

            {/* Recommended For You */}
            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Recommended for you</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={`recommended-${i}`} className="bg-white rounded shadow p-3 flex flex-col items-center">
                    <img src={tshirtFront} alt="Recommended product" className="w-full h-48 object-cover mb-3" />
                    <p className="text-sm font-semibold mb-1">Hoodies</p>
                    <p className="text-xs text-gray-500 mb-2">Starting at $19.99</p>
                    <button className="text-blue-600 text-sm font-medium hover:underline">Customize</button>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Link to="/templates" className="text-blue-600 font-medium hover:underline text-sm">
                  Browse all templates &rarr;
                </Link>
              </div>
            </div>
          </div>
        ) : (
          /* ========================
             STEP 2: CONFIRMATION
             ======================== */
          <div>
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                Thank You For Your Order! We’re Getting It Ready To Ship.
              </h1>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-8">
              <div className="bg-white rounded shadow p-4 flex justify-center items-center">
                <img src={tshirtFront} alt="Your product" className="max-h-80 object-contain" />
              </div>
              <div className="bg-white rounded shadow p-4">
                <h2 className="text-lg md:text-xl font-bold text-white bg-blue-700 rounded p-3 mb-4 text-center">
                  Payment Successful!
                </h2>
                <div className="mb-4 text-sm md:text-base">
                  <h3 className="font-semibold text-gray-700 mb-2">Order Summary</h3>
                  <div className="flex justify-between mb-1">
                    <span>Order Number</span>
                    <span className="font-medium">#3234557</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Total Amount</span>
                    <span className="font-medium">$39.00</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Estimated Delivery</span>
                    <span className="font-medium">Feb 15, 2025</span>
                  </div>
                </div>
                <div className="mb-4 text-sm md:text-base">
                  <h3 className="font-semibold text-gray-700 mb-2">Shipping Address</h3>
                  <p className="text-gray-800">
                    Jhon Febri
                    <br />
                    123, Main Street, Network, America
                  </p>
                </div>
                <Link to="/order" className="w-full block bg-yellow-500 text-white font-bold py-2 rounded hover:bg-yellow-600 text-center">
                  Track Order
                </Link>
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 text-center mb-8">
                Recommended for You
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div className="bg-white rounded shadow p-4 flex flex-col items-center">
                  <img src={recommended1} alt="Recommended item 1" className="w-full h-48 object-cover mb-3" />
                  <p className="text-sm font-semibold mb-1 text-gray-700">Most Customized • 3,400+ Orders</p>
                  <p className="text-xs text-gray-500 mb-2">4.8(250 Reviews) | Ships in 24h</p>
                  <button className="bg-yellow-500 text-white text-sm font-medium py-1 px-3 rounded hover:bg-yellow-600">Start Customizing</button>
                </div>
                <div className="bg-white rounded shadow p-4 flex flex-col items-center">
                  <img src={recommended2} alt="Recommended item 2" className="w-full h-48 object-cover mb-3" />
                  <p className="text-sm font-semibold mb-1 text-gray-700">Most Customized • 1,800+ Orders</p>
                  <p className="text-xs text-gray-500 mb-2">4.7(190 Reviews) | Ships in 24h</p>
                  <button className="bg-yellow-500 text-white text-sm font-medium py-1 px-3 rounded hover:bg-yellow-600">Start Customizing</button>
                </div>
                <div className="bg-white rounded shadow p-4 flex flex-col items-center">
                  <img src={recommended3} alt="Recommended item 3" className="w-full h-48 object-cover mb-3" />
                  <p className="text-sm font-semibold mb-1 text-gray-700">Most Customized • 2,600+ Orders</p>
                  <p className="text-xs text-gray-500 mb-2">4.9(300 Reviews) | Ships in 24h</p>
                  <button className="bg-yellow-500 text-white text-sm font-medium py-1 px-3 rounded hover:bg-yellow-600">Start Customizing</button>
                </div>
              </div>
              <div className="text-center">
                <Link to="/templates" className="text-blue-600 font-medium hover:underline text-sm">
                  Browse all templates &rarr;
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment;
